import { useQueryClient } from "@tanstack/react-query";
import {
  CheckIcon,
  CopyIcon,
  EyeIcon,
  LinkIcon,
  Loader2,
  SendIcon,
  ShieldCheckIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetCaseGuestsQueryKey,
  useGetCaseGuests,
  useRevokeGuestAccess,
  useShareCase,
} from "~/api/users/users";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";

interface ShareCaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  caseId: string;
  chatId: string;
  caseName?: string;
}

export const ShareCaseDialog: FC<ShareCaseDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  caseId,
  chatId,
  caseName,
}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [newlyCreatedGuestId, setNewlyCreatedGuestId] = useState<string | null>(
    null
  );
  const { handleCopy } = useCopyContent();

  // Fetch existing guests
  const { data: guests, isLoading: isLoadingGuests } = useGetCaseGuests(
    organizationId,
    caseId,
    caseId,
    {
      query: {
        enabled: open,
      },
    }
  );

  // Check if email already exists in guests list
  const isDuplicateEmail = useMemo(() => {
    if (!email.trim() || !guests) return false;
    const normalizedEmail = email.toLowerCase().trim();
    return guests.some(
      (guest) =>
        guest.email?.replace(/\+guest@/, "@").toLowerCase() === normalizedEmail
    );
  }, [email, guests]);

  const { mutate: shareCase, isPending } = useShareCase({
    mutation: {
      onSuccess: (data) => {
        trackEvent({ eventName: "app/doc_list/share_case/success" });
        setEmail("");
        setError(null);
        // Set the newly created guest ID to highlight it
        if (data?.accessId) {
          setNewlyCreatedGuestId(data.accessId);
        }
        // Refresh the guests list
        queryClient.invalidateQueries({
          queryKey: getGetCaseGuestsQueryKey(organizationId, caseId, caseId),
        });
      },
      onError: () => {
        trackEvent({ eventName: "app/doc_list/share_case/error" });
        setError(
          intl.formatMessage({
            id: "share.error.generic",
            defaultMessage: "Failed to create share link. Please try again.",
          })
        );
      },
    },
  });

  const { mutate: revokeAccess, isPending: isRevoking } = useRevokeGuestAccess({
    mutation: {
      onSuccess: () => {
        trackEvent({ eventName: "app/doc_list/share_case/revoke_success" });
        // Refresh the guests list
        queryClient.invalidateQueries({
          queryKey: getGetCaseGuestsQueryKey(organizationId, caseId, caseId),
        });
      },
      onError: () => {
        trackEvent({ eventName: "app/doc_list/share_case/revoke_error" });
      },
    },
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    if (!email.trim()) {
      setError(
        intl.formatMessage({
          id: "share.error.emailRequired",
          defaultMessage: "Email is required",
        })
      );
      return;
    }

    if (!validateEmail(email)) {
      setError(
        intl.formatMessage({
          id: "share.error.invalidEmail",
          defaultMessage: "Please enter a valid email address",
        })
      );
      return;
    }

    trackEvent({ eventName: "app/doc_list/share_case/submit" });
    shareCase({
      organizationId,
      projectId: caseId,
      data: {
        caseId,
        chatId,
        email,
      },
    });
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    setCopiedId(null);
    setNewlyCreatedGuestId(null);
    onOpenChange(false);
  };

  const handleCopyLink = (guestId: string, shareUrl: string) => {
    handleCopy(shareUrl);
    setCopiedId(guestId);
    setTimeout(() => setCopiedId(null), 2000);
    trackEvent({ eventName: "app/doc_list/share_case/copy_link" });
  };

  const handleRevoke = (accessId: string) => {
    revokeAccess({
      organizationId,
      projectId: caseId,
      caseId,
      accessId,
    });
  };

  const hasGuests = guests && guests.length > 0;
  const newlyCreatedGuest = guests?.find((g) => g.id === newlyCreatedGuestId);
  const showInitialFlow = !isLoadingGuests && !hasGuests && !newlyCreatedGuest;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id="share.title" defaultMessage="Guest Access" />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="share.description"
              defaultMessage="View-only access to {caseName}"
              values={{
                caseName: <strong className="font-semibold">{caseName}</strong>,
              }}
            />
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* 4-step visual flow - only shown when no guests exist */}
          {showInitialFlow && (
            <div className="bg-muted rounded-lg px-4 py-3">
              <div className="flex items-start justify-between">
                <div className="flex flex-col items-center text-center w-16">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-1.5">
                    <LinkIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    <FormattedMessage
                      id="share.step.createLink"
                      defaultMessage="Create link"
                    />
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center pt-5">
                  <svg className="w-5 h-3 text-border" viewBox="0 0 20 12">
                    <path
                      d="M0 6h16m0 0l-4-4m4 4l-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex flex-col items-center text-center w-16">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mb-1.5">
                    <SendIcon className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-xs text-amber-600 font-medium whitespace-nowrap">
                    <FormattedMessage
                      id="share.step.youSendIt"
                      defaultMessage="You send it"
                    />
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center pt-5">
                  <svg className="w-5 h-3 text-border" viewBox="0 0 20 12">
                    <path
                      d="M0 6h16m0 0l-4-4m4 4l-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex flex-col items-center text-center w-16">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-1.5">
                    <ShieldCheckIcon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    <FormattedMessage
                      id="share.step.theyVerify"
                      defaultMessage="They verify"
                    />
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center pt-5">
                  <svg className="w-5 h-3 text-border" viewBox="0 0 20 12">
                    <path
                      d="M0 6h16m0 0l-4-4m4 4l-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex flex-col items-center text-center w-16">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-1.5">
                    <EyeIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    <FormattedMessage
                      id="share.step.theyView"
                      defaultMessage="They view"
                    />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Newly created link success state */}
          {newlyCreatedGuest && (
            <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckIcon className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    {newlyCreatedGuest.email?.replace(/\+guest@/, "@")}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-red-500"
                  onClick={() => handleRevoke(newlyCreatedGuest.id!)}
                  disabled={isRevoking}
                >
                  <Trash2Icon className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 bg-background rounded-md p-2.5 border border-green-200 mb-3">
                <span className="text-xs text-green-700 truncate flex-1">
                  {newlyCreatedGuest.shareUrl}
                </span>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-3 py-1.5 h-auto shrink-0"
                  onClick={() =>
                    handleCopyLink(
                      newlyCreatedGuest.id!,
                      newlyCreatedGuest.shareUrl!
                    )
                  }
                >
                  {copiedId === newlyCreatedGuest.id ? (
                    <CheckIcon className="w-3.5 h-3.5 mr-1.5" />
                  ) : (
                    <CopyIcon className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  <FormattedMessage id="copy" defaultMessage="Copy" />
                </Button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded px-2.5 py-2 flex items-center justify-between">
                <span className="text-xs text-amber-800">
                  <FormattedMessage
                    id="share.sendInstruction"
                    defaultMessage="Send via email, SMS, or message."
                  />
                </span>
                <a
                  href="https://screen.studio/share/WnqQ4c7q"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-amber-700 underline hover:text-amber-900 shrink-0 ml-2"
                >
                  <FormattedMessage
                    id="share.seeWhatTheySee"
                    defaultMessage="What they'll see"
                  />
                </a>
              </div>
            </div>
          )}

          {/* Multiple guests list - shown when there are guests but not showing the newly created one prominently */}
          {hasGuests && !newlyCreatedGuest && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                  <FormattedMessage
                    id="share.peopleWithAccess"
                    defaultMessage="People with access"
                  />
                </span>
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="share.totalCount"
                    defaultMessage="{count} total"
                    values={{ count: guests.length }}
                  />
                </span>
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {guests.map((guest) => (
                  <div
                    key={guest.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-accent"
                  >
                    <span className="text-sm text-foreground">
                      {guest.email?.replace(/\+guest@/, "@")}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-muted-foreground hover:bg-accent"
                        onClick={() =>
                          handleCopyLink(guest.id!, guest.shareUrl!)
                        }
                      >
                        {copiedId === guest.id ? (
                          <CheckIcon className="w-3.5 h-3.5" />
                        ) : (
                          <CopyIcon className="w-3.5 h-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-muted-foreground hover:bg-red-100 hover:text-red-500"
                        onClick={() => handleRevoke(guest.id!)}
                        disabled={isRevoking}
                      >
                        <Trash2Icon className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing guests list when newly created guest is shown */}
          {hasGuests && newlyCreatedGuest && guests.length > 1 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                  <FormattedMessage
                    id="share.peopleWithAccess"
                    defaultMessage="People with access"
                  />
                </span>
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="share.totalCount"
                    defaultMessage="{count} total"
                    values={{ count: guests.length }}
                  />
                </span>
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {guests
                  .filter((g) => g.id !== newlyCreatedGuestId)
                  .map((guest) => (
                    <div
                      key={guest.id}
                      className="flex items-center justify-between px-3 py-2 hover:bg-accent"
                    >
                      <span className="text-sm text-foreground">
                        {guest.email?.replace(/\+guest@/, "@")}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-muted-foreground hover:bg-accent"
                          onClick={() =>
                            handleCopyLink(guest.id!, guest.shareUrl!)
                          }
                        >
                          {copiedId === guest.id ? (
                            <CheckIcon className="w-3.5 h-3.5" />
                          ) : (
                            <CopyIcon className="w-3.5 h-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-muted-foreground hover:bg-red-100 hover:text-red-500"
                          onClick={() => handleRevoke(guest.id!)}
                          disabled={isRevoking}
                        >
                          <Trash2Icon className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Email input section */}
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="share-email"
              className="text-sm text-muted-foreground"
            >
              <FormattedMessage
                id={hasGuests ? "share.addAnother" : "share.allowGuestToView"}
                defaultMessage={
                  hasGuests ? "Add another" : "Allow this guest to view"
                }
              />
            </Label>
            <Input
              id="share-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder={intl.formatMessage({
                id: "share.emailPlaceholder",
                defaultMessage: "colleague@example.com",
              })}
              className={
                isDuplicateEmail
                  ? "border-2 border-amber-400 bg-amber-50"
                  : error
                    ? "border-red-500"
                    : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isDuplicateEmail) {
                  handleSubmit();
                }
              }}
            />
            {isDuplicateEmail && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <TriangleAlertIcon className="w-3.5 h-3.5" />
                <FormattedMessage
                  id="share.duplicateEmail"
                  defaultMessage="Link already exists for this email. Copy it above."
                />
              </p>
            )}
            {error && !isDuplicateEmail && (
              <p className="text-xs text-red-500">{error}</p>
            )}
          </div>

          {/* Submit button */}
          <Button
            onClick={handleSubmit}
            disabled={isPending || isDuplicateEmail}
            className={
              isDuplicateEmail
                ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted"
                : ""
            }
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage
              id="share.createLink"
              defaultMessage="Create Link"
            />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
